/**
 * Générateur — Attendu 656 — Comprendre la définition d'un cercle et celle
 * d'un disque sous la forme d'ensembles de points.
 * Niveau : 6ème | Cluster : 30 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - position_point_qcm_ensemble        : QCM 4 choix sur la position d'un
 *                                           point (3 régimes : OM=r, OM<r, OM>r),
 *                                           sans figure
 *   - classement_points_vf_figure        : V/F (3V+3F stricte) sur figure SVG
 *                                           d'un cercle + 4 points
 *   - zone_double_qcm_figure             : QCM 4 choix sur figure SVG de 2
 *                                           cercles sécants + 5 points
 *   - compter_points_zone_saisie         : saisie numérique (compte de points
 *                                           dans une zone) sur figure SVG
 *   - ensemble_points_programme          : programme_construction 3 tâches
 *                                           (rayon numérique, rayon symbolique
 *                                           BM=BC, distance DE de F)
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_656.md et docs/fiches/PAYLOADS_656.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 656;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_POSITION = 'Position d\'un point (QCM)';
const L_CLASSEMENT_VF = 'Classement V/F sur figure';
const L_ZONE_DOUBLE = 'Zone double (QCM figure)';
const L_COMPTER = 'Compter points dans une zone';
const L_CONSTRUCTION = 'Construction de l\'ensemble';

// Lettres valides pour nommer les points (exclut I et O : ambiguïté avec 1 et 0).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Constantes SVG (programme_construction — alignées avec gen_655 / gen_patron.js)
const PX_PER_CM = 30;
const VIEWBOX_PC = { w: 600, h: 400 };
const TOLERANCE_PC = 12;

// Couleurs pour les tâches (programme_construction)
const COLOR_BLEU = '#378ADD';
const COLOR_ROUGE = '#E24B4A';
const COLOR_VERT = '#1D9E75';

// Couleurs pour les cercles de l'Ex. 3
const COLOR_VIOLET = '#9333EA';
const COLOR_ORANGE = '#F97316';

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Formate un nombre à la française (virgule décimale). */
const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    return String(n).replace('.', ',');
};

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Distance euclidienne entre deux points {x,y}. */
const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);

/**
 * Tente de placer un point à une distance dans [dMin, dMax] du centre,
 * en respectant les bornes du viewBox (avec marge).
 * @returns {{x:number, y:number, d:number}|null}
 */
const pickPointAtDistance = (cx, cy, dMin, dMax, bounds, maxAttempts = 80) => {
    for (let i = 0; i < maxAttempts; i++) {
        const d = dMin + Math.random() * (dMax - dMin);
        const angle = Math.random() * 2 * Math.PI;
        const x = cx + d * Math.cos(angle);
        const y = cy + d * Math.sin(angle);
        if (x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY) {
            return { x, y, d };
        }
    }
    return null;
};

/** Largeur approximative d'une majuscule à font-size 14 (police système). */
const GLYPHE_PX = 10;

/**
 * Calcule la position du label d'un point : décalage radial de `offset` px.
 *
 * `cadre` (optionnel, { w, h }) borne le résultat À L'INTÉRIEUR du viewBox. Sans
 * lui, un point placé en limite de zone envoyait sa lettre au-delà du bord :
 * le décalage radial de 12 px s'ajoute à la marge de placement, et le glyphe
 * lui-même occupe encore ~10 px. Mesuré sur l'Ex. 4 : 3,1 % des libellés
 * débordaient, jusqu'à 7 px. Le décalage vertical minimal de 12 px correspond à
 * la hauteur d'ascendante : au-dessus, la lettre serait coupée par le haut.
 */
const labelPosRadial = (cx, cy, px, py, offset = 12, cadre = null) => {
    const vx = px - cx;
    const vy = py - cy;
    const L = Math.hypot(vx, vy) || 1;
    let x = px + (vx / L) * offset;
    let y = py + (vy / L) * offset + 4;
    if (cadre) {
        x = Math.min(Math.max(x, 1), cadre.w - GLYPHE_PX);
        y = Math.min(Math.max(y, 12), cadre.h - 2);
    }
    return { x: +x.toFixed(1), y: +y.toFixed(1) };
};

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Position d'un point (QCM sans figure)
// ═══════════════════════════════════════════════════════════

/** Construit les 4 options QCM à partir du nom du point M. */
const buildChoixPosition = (M) => ({
    A: `Le point ${M} est à l'intérieur du disque (mais pas sur le cercle).`,
    B: `Le point ${M} est sur le cercle.`,
    C: `Le point ${M} est à l'extérieur du disque.`,
    D: `On ne peut pas savoir avec ces informations.`,
});

const genPositionPointQcmEnsemble = () => {
    const [Ocenter, M] = pickN(2);
    const r = randInt(2, 8);
    const regime = randomChoice(['eq', 'inf', 'sup']);

    let OM;
    let bonneKey;
    if (regime === 'eq') {
        OM = r;
        bonneKey = 'B';
    } else if (regime === 'inf') {
        OM = randInt(0, r - 1);
        bonneKey = 'A';
    } else {
        OM = r + randInt(1, 4);
        bonneKey = 'C';
    }

    const choixMap = buildChoixPosition(M);
    const choix = [choixMap.A, choixMap.B, choixMap.C, choixMap.D];
    const bonne = choixMap[bonneKey];

    // Préambule pédagogique : si OM = 0, préciser "M est le centre" (cas limite).
    // « est tel que » et non « vérifie » : la seconde tournure est de niveau lycée.
    let secondPara;
    if (OM === 0) {
        secondPara = `<p>Le point ${M} est confondu avec le centre ${Ocenter} (donc ${Ocenter}${M} = 0 cm).</p>`;
    } else {
        secondPara = `<p>Le point ${M} est tel que ${Ocenter}${M} = ${OM} cm.</p>`;
    }

    const texte = `<p>Un cercle a pour centre ${Ocenter} et pour rayon ${r} cm.</p>${secondPara}<p>Où se situe ${M} par rapport à ce cercle ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_POSITION));
    payload._meta.generatorKey = 'position_point_qcm_ensemble';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Classement V/F sur figure (cercle + 4 points)
// ═══════════════════════════════════════════════════════════

const EX2_VIEWBOX = { w: 240, h: 200 };
const EX2_CENTER = { x: 120, y: 100 };
const EX2_PX_PER_CM = 18; // échelle locale (différente de PX_PER_CM=30 pour Ex. 5)
const EX2_MARGIN = 15;

/** Pool des affirmations VRAIES instanciées avec les noms réels. */
const POOL_VRAIES_VF656 = (Psurle, Pinterieur, Pexterieur, Ocenter, rCmDisplay) => [
    `${Psurle} appartient au cercle.`,
    `${Psurle} appartient au disque.`,
    `${Pinterieur} appartient au disque.`,
    `${Pexterieur} est à l'extérieur du disque.`,
    `La distance ${Ocenter}${Psurle} mesure exactement ${rCmDisplay} cm.`,
    `${Ocenter} appartient au disque.`,
    `Tout point du cercle appartient au disque.`,
];

/** Pool des affirmations FAUSSES instanciées avec les noms réels. */
const POOL_FAUSSES_VF656 = (Psurle, Pinterieur, Pexterieur, Ocenter) => [
    `${Ocenter} appartient au cercle.`,
    `${Pinterieur} est à l'extérieur du disque.`,
    `${Pexterieur} appartient au disque.`,
    `${Psurle} est à l'intérieur du disque (mais pas sur le cercle).`,
    `Tout point du disque appartient au cercle.`,
    `Deux points distincts du cercle n'ont pas la même distance au centre.`,
];

/**
 * Construit le SVG figure Ex. 2 : cercle + centre + 4 points avec labels.
 */
const buildFigureEx2Svg = (Ocenter, pts, rPx) => {
    const { x: cx, y: cy } = EX2_CENTER;
    const ocLabel = labelPosRadial(cx, cy, cx - 8, cy - 8, 4); // centre : label en haut-gauche
    const body =
        `<circle cx="${cx}" cy="${cy}" r="${rPx.toFixed(1)}" stroke="#000" fill="none" stroke-width="2" />`
        + `<circle cx="${cx}" cy="${cy}" r="2" fill="#000" />`
        + pts.map(p => {
            const L = labelPosRadial(cx, cy, p.x, p.y, 12, EX2_VIEWBOX);
            return `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.5" fill="#000" />`
                + `<text x="${L.x}" y="${L.y}" font-size="14" fill="#000">${p.name}</text>`;
        }).join('')
        + `<text x="${(cx - 12).toFixed(1)}" y="${(cy + 4).toFixed(1)}" font-size="14" fill="#000">${Ocenter}</text>`;
    // ocLabel hors champ : variable conservée pour documenter le calcul, non utilisée
    void ocLabel;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${EX2_VIEWBOX.w} ${EX2_VIEWBOX.h}">${body}</svg>`;
};

/** Place les 4 points Psurle, Pinterieur, Pexterieur, P4 dans les zones requises. */
const placerPointsEx2 = (names, rPx) => {
    const { x: cx, y: cy } = EX2_CENTER;
    const bounds = {
        minX: EX2_MARGIN, maxX: EX2_VIEWBOX.w - EX2_MARGIN,
        minY: EX2_MARGIN, maxY: EX2_VIEWBOX.h - EX2_MARGIN,
    };

    const maxTries = 100;
    for (let attempt = 0; attempt < maxTries; attempt++) {
        // P1 : sur le cercle (distance = rPx)
        const p1 = pickPointAtDistance(cx, cy, rPx, rPx, bounds);
        // P2 : strict intérieur (distance ∈ [rPx*0.25, rPx*0.75])
        const p2 = pickPointAtDistance(cx, cy, rPx * 0.25, rPx * 0.75, bounds);
        // P3 : strict extérieur (distance ∈ [rPx+15, rPx+30])
        const p3 = pickPointAtDistance(cx, cy, rPx + 15, rPx + 30, bounds);
        // P4 : zone aléatoire parmi les 3
        const zone4 = randomChoice(['in', 'on', 'out']);
        let p4;
        if (zone4 === 'on') {
            p4 = pickPointAtDistance(cx, cy, rPx, rPx, bounds);
        } else if (zone4 === 'in') {
            p4 = pickPointAtDistance(cx, cy, rPx * 0.25, rPx * 0.75, bounds);
        } else {
            p4 = pickPointAtDistance(cx, cy, rPx + 15, rPx + 30, bounds);
        }
        if (!p1 || !p2 || !p3 || !p4) continue;

        const all = [
            { name: names[0], ...p1, zone: 'on' },
            { name: names[1], ...p2, zone: 'in' },
            { name: names[2], ...p3, zone: 'out' },
            { name: names[3], ...p4, zone: zone4 },
        ];
        // Distance deux-à-deux ≥ 18 px (éviter chevauchement de labels)
        let ok = true;
        for (let i = 0; i < 4 && ok; i++) {
            for (let j = i + 1; j < 4 && ok; j++) {
                if (dist(all[i], all[j]) < 18) ok = false;
            }
        }
        if (ok) return all;
    }
    // Fallback déterministe
    return [
        { name: names[0], x: cx + rPx, y: cy, zone: 'on' },
        { name: names[1], x: cx - rPx * 0.5, y: cy, zone: 'in' },
        { name: names[2], x: cx, y: cy - (rPx + 22), zone: 'out' },
        { name: names[3], x: cx, y: cy + (rPx + 22), zone: 'out' },
    ];
};

const genClassementPointsVfFigure = () => {
    // 5 lettres : P1=Psurle, P2=Pinterieur, P3=Pexterieur, P4=extra, Ocenter
    const [n1, n2, n3, n4, Ocenter] = pickN(5);

    const rCm = randomChoice([2, 2.5, 3, 3.5, 4]);
    const rPx = rCm * EX2_PX_PER_CM;

    const pts = placerPointsEx2([n1, n2, n3, n4], rPx);
    const svg = buildFigureEx2Svg(Ocenter, pts, rPx);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    const Psurle = n1;
    const Pinterieur = n2;
    const Pexterieur = n3;

    const vraies = POOL_VRAIES_VF656(Psurle, Pinterieur, Pexterieur, Ocenter, fmt(rCm));
    const fausses = POOL_FAUSSES_VF656(Psurle, Pinterieur, Pexterieur, Ocenter);
    const chosenVraies = randomChoiceMultiple(vraies, 3);
    const chosenFausses = randomChoiceMultiple(fausses, 3);

    // SHUFFLE côté générateur — OBLIGATOIRE (makeVraiFaux ne mélange pas)
    const merged = shuffle([
        ...chosenVraies.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenFausses.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    // « placés sur la FIGURE », jamais « sur le cercle » : un seul des quatre
    // points est effectivement sur le cercle, les autres sont dedans ou dehors —
    // c'est précisément ce que l'exercice demande de déterminer. L'ancienne
    // formulation « le cercle sur lequel sont placés quatre points » l'affirmait
    // à tort dans 100 % des tirages. La consigne de cochage saute : les colonnes
    // Vrai/Faux la disent déjà.
    const texte = `<p>Le cercle ci-dessous a pour centre ${Ocenter} et pour rayon ${fmt(rCm)} cm. Les points ${n1}, ${n2}, ${n3} et ${n4} sont placés sur la figure.</p>{{element:0}}`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_CLASSEMENT_VF),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'classement_points_vf_figure';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Zone double (QCM sur figure 2 cercles sécants)
// ═══════════════════════════════════════════════════════════

const EX3_VIEWBOX = { w: 300, h: 200 };
const EX3_PX_PER_CM = 20;
const EX3_MARGIN = 20;

/**
 * Tire une configuration valide (A, B, r1, r2) telle que les cercles sont
 * sécants avec un chevauchement CONFORTABLE : marge 26 px de part et d'autre
 * de l'intervalle de sécance, pour que la lentille (in/in) et les deux
 * croissants (in/out, out/in) gardent ≥ 14 px utiles une fois retirée la
 * marge EPS (6 px) du placement des points. Avec l'ancienne marge de 8 px,
 * la lentille « sûre » pouvait être VIDE → placement impossible → fallback
 * géométriquement faux (bonne réponse hors du disque).
 * Retourne null si aucune config n'est trouvée (le caller re-tire).
 */
const tirerConfigEx3 = () => {
    const maxTries = 200;
    for (let i = 0; i < maxTries; i++) {
        const r1Cm = randomChoice([1.5, 2, 2.5, 3]);
        const r2Cm = randomChoice([1.5, 2, 2.5, 3]);
        const R1 = r1Cm * EX3_PX_PER_CM;
        const R2 = r2Cm * EX3_PX_PER_CM;

        // Construction directe : distance tirée DANS l'intervalle valide,
        // puis B placé à cette distance de A (angle faible → B à droite).
        const dMin = Math.abs(R1 - R2) + 26;
        const dMax = R1 + R2 - 26;
        if (dMin > dMax) continue;
        const d = randInt(dMin, dMax);

        const Ax = randInt(EX3_MARGIN + 20, EX3_VIEWBOX.w / 2);
        const Ay = randInt(EX3_MARGIN + 20, EX3_VIEWBOX.h - EX3_MARGIN - 20);
        const angle = (randInt(-25, 25) * Math.PI) / 180;
        const Bx = Math.round(Ax + d * Math.cos(angle));
        const By = Math.round(Ay + d * Math.sin(angle));
        if (Bx < EX3_VIEWBOX.w / 2 + 10 || Bx > EX3_VIEWBOX.w - EX3_MARGIN - 20) continue;
        if (By < EX3_MARGIN + 20 || By > EX3_VIEWBOX.h - EX3_MARGIN - 20) continue;

        // Les deux cercles doivent tenir ENTIÈREMENT dans le viewBox. Les tests
        // ci-dessus ne portent que sur la position des CENTRES : avec Ax = 40 et
        // R1 = 60 px (3 cm), le cercle sortait de 20 px à gauche. 10,8 % des
        // cercles étaient rognés. Or l'exercice demande de juger si un point est
        // DANS un disque : quand l'arc qui le borde disparaît du cadre, l'élève
        // perd le repère qui lui permet de trancher.
        const tientDansLeCadre = (cx, cy, r) =>
            cx - r >= 0 && cx + r <= EX3_VIEWBOX.w && cy - r >= 0 && cy + r <= EX3_VIEWBOX.h;
        if (!tientDansLeCadre(Ax, Ay, R1) || !tientDansLeCadre(Bx, By, R2)) continue;

        return { Ax, Ay, Bx, By, R1, R2, r1Cm, r2Cm };
    }
    return null;
};

/** Place 5 points dans les 4 zones (in/in, in/out, out/in, out/out) + 1 bonus. */
const placerPointsEx3 = (names, cfg) => {
    const { Ax, Ay, Bx, By, R1, R2 } = cfg;
    const bounds = {
        minX: EX3_MARGIN, maxX: EX3_VIEWBOX.w - EX3_MARGIN,
        minY: EX3_MARGIN, maxY: EX3_VIEWBOX.h - EX3_MARGIN,
    };
    const EPS = 6; // marge pour éviter les cas-bord

    const isInA = (p) => Math.hypot(p.x - Ax, p.y - Ay) < R1 - EPS;
    const isOutA = (p) => Math.hypot(p.x - Ax, p.y - Ay) > R1 + EPS;
    const isInB = (p) => Math.hypot(p.x - Bx, p.y - By) < R2 - EPS;
    const isOutB = (p) => Math.hypot(p.x - Bx, p.y - By) > R2 + EPS;

    const inZone = (p, zone) => {
        if (zone === 'in/in') return isInA(p) && isInB(p);
        if (zone === 'in/out') return isInA(p) && isOutB(p);
        if (zone === 'out/in') return isOutA(p) && isInB(p);
        if (zone === 'out/out') return isOutA(p) && isOutB(p);
        return false;
    };

    // Tire un point aléatoire dans le viewBox respectant la zone
    const tireZone = (zone, placed, maxTries = 400) => {
        for (let i = 0; i < maxTries; i++) {
            const x = randInt(bounds.minX, bounds.maxX);
            const y = randInt(bounds.minY, bounds.maxY);
            const p = { x, y };
            if (!inZone(p, zone)) continue;
            // Distance aux autres points ≥ 22 px
            if (placed.some(q => dist(p, q) < 22)) continue;
            return p;
        }
        return null;
    };

    const maxFullTries = 8;
    for (let attempt = 0; attempt < maxFullTries; attempt++) {
        const placed = [];
        const zones = ['in/in', 'in/out', 'out/in', 'out/out'];
        // + 1 zone bonus (aléatoire parmi les 4)
        const allZones = [...zones, randomChoice(zones)];
        const pts = [];
        let ok = true;
        for (let i = 0; i < 5; i++) {
            const z = allZones[i];
            const p = tireZone(z, placed);
            if (!p) { ok = false; break; }
            pts.push({ ...p, name: names[i], zone: z });
            placed.push(p);
        }
        if (ok) return pts;
    }
    // Échec : PAS de fallback approximatif ici (l'ancien plaçait le point
    // « in/in » au milieu des centres SANS vérifier — bonne réponse fausse
    // quand la lentille était fine). Le caller re-tire une autre config.
    return null;
};

/**
 * Fallback ultime de l'Ex. 3 : config + points FIGÉS, vérifiés à la main
 * (d = 100, R1 = R2 = 60, EPS = 6 respecté partout) :
 *   (150,100) → 50 des deux centres < 54            → in/in
 *   (70,100)  → 30 de A < 54 ; 130 de B > 66        → in/out
 *   (230,100) → symétrique                           → out/in
 *   (30,30)   → ≈ 99 de A > 66 ; ≈ 184 de B > 66     → out/out
 *   (270,170) → ≈ 184 de A > 66 ; ≈ 99 de B > 66     → out/out
 */
const FALLBACK_EX3 = {
    cfg: { Ax: 100, Ay: 100, Bx: 200, By: 100, R1: 60, R2: 60, r1Cm: 3, r2Cm: 3 },
    points: [
        { x: 150, y: 100, zone: 'in/in' },
        { x: 70, y: 100, zone: 'in/out' },
        { x: 230, y: 100, zone: 'out/in' },
        { x: 30, y: 30, zone: 'out/out' },
        { x: 270, y: 170, zone: 'out/out' },
    ],
};

/** Construit le SVG figure Ex. 3 : 2 cercles sécants + centres + 5 points. */
const buildFigureEx3Svg = (A_name, B_name, cfg, pts) => {
    const { Ax, Ay, Bx, By, R1, R2 } = cfg;
    const body =
        `<circle cx="${Ax}" cy="${Ay}" r="${R1.toFixed(1)}" stroke="${COLOR_VIOLET}" fill="none" stroke-width="2" />`
        + `<circle cx="${Bx}" cy="${By}" r="${R2.toFixed(1)}" stroke="${COLOR_ORANGE}" fill="none" stroke-width="2" />`
        + `<circle cx="${Ax}" cy="${Ay}" r="2" fill="#000" />`
        + `<circle cx="${Bx}" cy="${By}" r="2" fill="#000" />`
        + `<text x="${(Ax - 14).toFixed(1)}" y="${(Ay - 6).toFixed(1)}" font-size="14" fill="${COLOR_VIOLET}">${A_name}</text>`
        + `<text x="${(Bx + 6).toFixed(1)}" y="${(By - 6).toFixed(1)}" font-size="14" fill="${COLOR_ORANGE}">${B_name}</text>`
        + pts.map(p => {
            // Label décalé radialement par rapport au centre A (convention simple)
            const L = labelPosRadial(Ax, Ay, p.x, p.y, 11, EX3_VIEWBOX);
            return `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.5" fill="#000" />`
                + `<text x="${L.x}" y="${L.y}" font-size="14" fill="#000">${p.name}</text>`;
        }).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${EX3_VIEWBOX.w} ${EX3_VIEWBOX.h}">${body}</svg>`;
};

const EX3_QUESTIONS = [
    { id: 'Q1', zone: 'in/in',   phrase: (A, B) => `Quel point est situé <strong>à la fois</strong> dans le disque de centre ${A} et dans le disque de centre ${B} ?` },
    { id: 'Q2', zone: 'in/out',  phrase: (A, B) => `Quel point est situé dans le disque de centre ${A} <strong>et</strong> à l'extérieur du disque de centre ${B} ?` },
    { id: 'Q3', zone: 'out/in',  phrase: (A, B) => `Quel point est situé à l'extérieur du disque de centre ${A} <strong>et</strong> dans le disque de centre ${B} ?` },
    { id: 'Q4', zone: 'out/out', phrase: (A, B) => `Quel point est situé à l'extérieur des deux disques (centre ${A} et centre ${B}) ?` },
];

const genZoneDoubleQcmFigure = () => {
    // 2 lettres pour les centres + 5 pour les points = 7 distinctes
    const picked = pickN(7);
    const [A_name, B_name] = picked;
    const pointNames = picked.slice(2);

    // Config ET placement re-tirés ENSEMBLE : une config trop serrée ne
    // condamne plus le placement (cause du bug « bonne réponse hors disque »)
    let cfg = null;
    let pts = null;
    for (let t = 0; t < 40 && !pts; t++) {
        cfg = tirerConfigEx3();
        if (!cfg) continue;
        pts = placerPointsEx3(pointNames, cfg);
    }
    if (!pts) {
        cfg = FALLBACK_EX3.cfg;
        pts = FALLBACK_EX3.points.map((p, i) => ({ ...p, name: pointNames[i] }));
    }

    const question = randomChoice(EX3_QUESTIONS);

    // Points dans la zone ciblée
    const pointsInZone = pts.filter(p => p.zone === question.zone);
    // Si plusieurs points dans la zone ciblée (cas du bonus qui tombe sur la même zone),
    // en prendre un au hasard, et retirer les autres de la liste.
    const correctPoint = randomChoice(pointsInZone);
    // Les 3 autres choix : tirés parmi les points de zones différentes.
    const autres = pts.filter(p => p.name !== correctPoint.name && p.zone !== question.zone);
    const distracteurs = randomChoiceMultiple(autres, 3);
    // Si la zone bonus a doublonné la zone cible, `autres` peut avoir < 3 points.
    // Fallback : compléter avec des points de la zone cible autres que correct.
    if (distracteurs.length < 3) {
        const fallback = pts.filter(p => p.name !== correctPoint.name && !distracteurs.includes(p));
        while (distracteurs.length < 3 && fallback.length > 0) {
            distracteurs.push(fallback.shift());
        }
    }

    const choix = [correctPoint.name, ...distracteurs.map(p => p.name)];

    const svg = buildFigureEx3Svg(A_name, B_name, cfg, pts);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    const texte = `<p>Ci-dessous, un cercle de centre ${A_name} et de rayon ${fmt(cfg.r1Cm)} cm (en violet) et un cercle de centre ${B_name} et de rayon ${fmt(cfg.r2Cm)} cm (en orange).</p><p>${question.phrase(A_name, B_name)}</p>`;

    const payload = makeQcm(choix, correctPoint.name, texte, {
        ...opts(L_ZONE_DOUBLE),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'zone_double_qcm_figure';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Compter points dans une zone (saisie + figure)
// ═══════════════════════════════════════════════════════════

const EX4_VIEWBOX = { w: 240, h: 200 };
const EX4_CENTER = { x: 120, y: 100 };
const EX4_PX_PER_CM = 20;
const EX4_MARGIN = 15;
const EX4_BOUNDS = {
    minX: EX4_MARGIN, maxX: EX4_VIEWBOX.w - EX4_MARGIN,
    minY: EX4_MARGIN, maxY: EX4_VIEWBOX.h - EX4_MARGIN,
};

const EX4_NOMBRES_FR = { 6: 'six', 7: 'sept', 8: 'huit' };

const EX4_CONDITIONS = [
    { id: 'C1', phrase: (r, O) => `Combien de points sont <strong>à moins de ${fmt(r)} cm</strong> de ${O} ?`, pred: (d, R) => d < R - 3 },
    { id: 'C2', phrase: (r, O) => `Combien de points sont <strong>à plus de ${fmt(r)} cm</strong> de ${O} ?`, pred: (d, R) => d > R + 3 },
    { id: 'C3', phrase: (r, O, rCm) => `Combien de points appartiennent au cercle de centre ${O} et de rayon ${fmt(rCm)} cm ?`, pred: (d, R) => Math.abs(d - R) <= 1.5 },
];

/** Place N points dans le viewBox avec stratégie de forçage selon la condition. */
const placerPointsEx4 = (names, R, condId) => {
    const { x: cx, y: cy } = EX4_CENTER;
    const N = names.length;
    const maxTries = 80;

    for (let attempt = 0; attempt < maxTries; attempt++) {
        const placed = [];
        let ok = true;

        // Nombre de points "forcés" dans la zone cible : 1 ou 2.
        const nForce = randInt(1, 2);

        // Helper : pousser un point dans la zone cible
        const pushTarget = () => {
            let pt;
            if (condId === 'C1') {
                // borne haute R-10 : le point forcé reste hors de la bande
                // visuellement ambiguë autour du trait (cf. exclusion ±8)
                pt = pickPointAtDistance(cx, cy, Math.max(6, R * 0.2), R - 10, EX4_BOUNDS);
            } else if (condId === 'C2') {
                pt = pickPointAtDistance(cx, cy, R + 10, R + 30, EX4_BOUNDS);
            } else {
                pt = pickPointAtDistance(cx, cy, R, R, EX4_BOUNDS);
            }
            return pt;
        };

        // Placer les points forcés
        for (let k = 0; k < nForce && ok; k++) {
            const pt = pushTarget();
            if (!pt) { ok = false; break; }
            // distance ≥ 20 aux autres
            if (placed.some(q => dist(pt, q) < 20)) { ok = false; break; }
            placed.push(pt);
        }
        if (!ok) continue;

        // Placer les autres points : aléatoire évitant accidentellement le bord (± 3 px)
        for (let k = placed.length; k < N && ok; k++) {
            let pt = null;
            for (let attempt2 = 0; attempt2 < 60; attempt2++) {
                const x = randInt(EX4_BOUNDS.minX, EX4_BOUNDS.maxX);
                const y = randInt(EX4_BOUNDS.minY, EX4_BOUNDS.maxY);
                const cand = { x, y };
                // Distance min aux autres
                if (placed.some(q => dist(cand, q) < 20)) continue;
                // Zone d'exclusion autour du trait : un point aléatoire doit
                // être FRANCHEMENT dedans ou dehors (à 20 px/cm, 3 px = 1,5 mm
                // à l'écran : indécidable à l'œil). Pour C3 le comptage exige
                // |d-R| ≤ 1,5 → on interdit la bande ambiguë (1,5 ; 10).
                const d = Math.hypot(x - cx, y - cy);
                if (condId === 'C3' && Math.abs(d - R) > 1.5 && Math.abs(d - R) < 10) continue;
                if (condId !== 'C3' && Math.abs(d - R) <= 8) continue;
                pt = cand;
                break;
            }
            if (!pt) { ok = false; break; }
            placed.push(pt);
        }
        if (!ok) continue;

        const pts = placed.map((p, i) => ({ ...p, name: names[i] }));

        // Vérifie que la réponse calculée ≥ 1
        const cond = EX4_CONDITIONS.find(c => c.id === condId);
        const count = pts.filter(p => cond.pred(Math.hypot(p.x - cx, p.y - cy), R)).length;
        if (count >= 1) {
            return { pts, count };
        }
    }

    // Fallback déterministe : 1 point forcé + autres aléatoires
    const pts = names.map((n, i) => {
        if (i === 0) {
            // point forcé dans la zone cible
            if (condId === 'C1') return { name: n, x: cx + R * 0.4, y: cy };
            if (condId === 'C2') return { name: n, x: cx + R + 15, y: cy };
            return { name: n, x: cx + R, y: cy };
        }
        const angle = (i / names.length) * 2 * Math.PI;
        const d = R + 22 * (i % 2 === 0 ? 1 : -0.5);
        return { name: n, x: cx + d * Math.cos(angle), y: cy + d * Math.sin(angle) };
    });
    const cond = EX4_CONDITIONS.find(c => c.id === condId);
    // Compte HONNÊTE depuis les positions réelles — jamais de Math.max(1, …) :
    // le point forcé satisfait la condition par construction (vérifié C1/C2/C3),
    // et si un jour il ne la satisfaisait plus, mieux vaut une réponse 0 juste
    // qu'une réponse 1 fausse.
    const count = pts.filter(p => cond.pred(Math.hypot(p.x - cx, p.y - cy), R)).length;
    return { pts, count };
};

/** Construit le SVG figure Ex. 4 : cercle + centre + N points. */
const buildFigureEx4Svg = (Ocenter, pts, rPx) => {
    const { x: cx, y: cy } = EX4_CENTER;
    const body =
        `<circle cx="${cx}" cy="${cy}" r="${rPx.toFixed(1)}" stroke="#000" fill="none" stroke-width="2" />`
        + `<circle cx="${cx}" cy="${cy}" r="2" fill="#000" />`
        + pts.map(p => {
            const L = labelPosRadial(cx, cy, p.x, p.y, 12, EX4_VIEWBOX);
            return `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2.5" fill="#000" />`
                + `<text x="${L.x}" y="${L.y}" font-size="14" fill="#000">${p.name}</text>`;
        }).join('')
        + `<text x="${(cx - 12).toFixed(1)}" y="${(cy + 4).toFixed(1)}" font-size="14" fill="#000">${Ocenter}</text>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${EX4_VIEWBOX.w} ${EX4_VIEWBOX.h}">${body}</svg>`;
};

const genCompterPointsZoneSaisie = () => {
    const N = randomChoice([6, 7, 8]);
    const picked = pickN(N + 1); // 1 centre + N points
    const Ocenter = picked[0];
    const pointNames = picked.slice(1);

    const rCm = randomChoice([2, 2.5, 3, 3.5]);
    const rPx = rCm * EX4_PX_PER_CM;

    const cond = randomChoice(EX4_CONDITIONS);

    const { pts, count } = placerPointsEx4(pointNames, rPx, cond.id);

    const svg = buildFigureEx4Svg(Ocenter, pts, rPx);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    const nombreFr = EX4_NOMBRES_FR[N] || String(N);
    const preambule = `<p>La figure ci-dessous représente un cercle de centre ${Ocenter} et de rayon ${fmt(rCm)} cm, ainsi que ${nombreFr} points nommés (${pts.map(p => p.name).join(', ')}).</p>`;
    const question = `<p>${cond.phrase(rCm, Ocenter, rCm)}</p>`;
    const texte = preambule + question;

    const payload = makeSaisie(String(count), texte, {
        ...opts(L_COMPTER),
        suffixe: ' point(s)',
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'compter_points_zone_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Construction de l'ensemble (programme_construction)
// ═══════════════════════════════════════════════════════════

/**
 * Tire 6 points {name, x, y} aléatoirement dans le viewBox, avec contraintes :
 *   - marge ≥ 60 px des bords
 *   - distance deux-à-deux ≥ 55 px
 *   - distance P2P3 ∈ [60, 180] px (T2 : rayon symbolique BM = BC)
 *   - distance P4P5 ∈ [60, 150] px (T3 : rayon = distance DE)
 *   - cercle(P6, dist(P4, P5)) reste dans le viewBox (T3)
 *   - cercle(P1, r1Px) reste dans le viewBox (T1)
 *   - cercle(P2, dist(P2, P3)) reste dans le viewBox (T2)
 */
const tirer6PointsConstruction656 = (r1Px) => {
    const names = pickN(6);
    const margin = 60;
    const W = VIEWBOX_PC.w;
    const H = VIEWBOX_PC.h;
    const maxAttempts = 500;
    const bboxMargin = 10;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const pts = names.map(name => ({
            name,
            x: randInt(margin, W - margin),
            y: randInt(margin, H - margin),
        }));
        // Distance deux-à-deux ≥ 55 px
        let ok = true;
        for (let i = 0; i < 6 && ok; i++) {
            for (let j = i + 1; j < 6 && ok; j++) {
                if (dist(pts[i], pts[j]) < 55) ok = false;
            }
        }
        if (!ok) continue;

        // T1 : cercle(P1, r1Px) dans viewBox
        const [P1, P2, P3, P4, P5, P6] = pts;
        if (P1.x - r1Px < bboxMargin || P1.x + r1Px > W - bboxMargin) continue;
        if (P1.y - r1Px < bboxMargin || P1.y + r1Px > H - bboxMargin) continue;

        // T2 : rayon symbolique dist(P2, P3) ∈ [60, 180]
        const d23 = dist(P2, P3);
        if (d23 < 60 || d23 > 180) continue;
        if (P2.x - d23 < bboxMargin || P2.x + d23 > W - bboxMargin) continue;
        if (P2.y - d23 < bboxMargin || P2.y + d23 > H - bboxMargin) continue;

        // T3 : rayon dist(P4, P5) ∈ [60, 150]
        const d45 = dist(P4, P5);
        if (d45 < 60 || d45 > 150) continue;
        // cercle(P6, d45) dans viewBox
        if (P6.x - d45 < bboxMargin || P6.x + d45 > W - bboxMargin) continue;
        if (P6.y - d45 < bboxMargin || P6.y + d45 > H - bboxMargin) continue;

        return pts;
    }
    // Fallback déterministe : configuration fixe validée
    // (P6 à y=280 : cercle T3 de rayon ≈ 91 → 280+91 = 371 ≤ 390, dans le viewBox)
    return [
        { name: names[0], x: 150, y: 200 }, // P1 : centre T1, r1=60..120
        { name: names[1], x: 450, y: 120 }, // P2 : centre T2
        { name: names[2], x: 480, y: 220 }, // P3 : dist P2P3 ≈ 105
        { name: names[3], x: 200, y: 330 }, // P4
        { name: names[4], x: 290, y: 340 }, // P5 : dist P4P5 ≈ 90
        { name: names[5], x: 400, y: 280 }, // P6 : centre T3
    ];
};

const genEnsemblePointsProgramme = () => {
    // T1 : rayon numérique
    const r1Cm = randomChoice([2, 3, 4]);
    const r1Px = r1Cm * PX_PER_CM;

    const pts = tirer6PointsConstruction656(r1Px);
    const [P1, P2, P3, P4, P5, P6] = pts;

    // T2 : rayon symbolique = distance P2P3 (px, arrondi entier)
    const r2Px = Math.round(dist(P2, P3));
    // T3 : rayon = distance P4P5 (px, arrondi), centre = P6
    const r3Px = Math.round(dist(P4, P5));

    const points = pts.map(p => ({ name: p.name, x: p.x, y: p.y }));

    const tasks = [
        {
            id: 'a',
            consigne: `Trace en <b>bleu</b> l'ensemble des points situés à ${r1Cm} cm de ${P1.name}.`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: P1.x, cy: P1.y, r: r1Px },
        },
        {
            id: 'b',
            consigne: `Trace en <b>rouge</b> l'ensemble des points M tels que ${P2.name}M = ${P2.name}${P3.name}.`,
            color: COLOR_ROUGE,
            shape: { type: 'circle', cx: P2.x, cy: P2.y, r: r2Px },
        },
        {
            id: 'c',
            consigne: `Trace en <b>vert</b> l'ensemble des points situés à la distance ${P4.name}${P5.name} du point ${P6.name}.`,
            color: COLOR_VERT,
            shape: { type: 'circle', cx: P6.x, cy: P6.y, r: r3Px },
        },
    ];

    // Texte OBLIGATOIRE (le serializer back refuse un énoncé vide au POST —
    // revue 2026-08-11) : une phrase de CADRAGE qui pose la notion du format
    // (le cercle comme ENSEMBLE de points), sans répéter les consignes.
    const texte = '<p>Chaque consigne décrit un ensemble de points : trace-le de la couleur indiquée.</p>';

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOLERANCE_PC,
            viewBox: VIEWBOX_PC,
            points,
            preShapes: [],
            tasks,
        },
        texte,
        opts(L_CONSTRUCTION),
    );
    payload._meta.generatorKey = 'ensemble_points_programme';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    position_point_qcm_ensemble:   genPositionPointQcmEnsemble,
    classement_points_vf_figure:   genClassementPointsVfFigure,
    zone_double_qcm_figure:        genZoneDoubleQcmFigure,
    compter_points_zone_saisie:    genCompterPointsZoneSaisie,
    ensemble_points_programme:     genEnsemblePointsProgramme,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', qcm_images: 'QCM images',
    qcm_ensemble: 'QCM', qcm_figure: 'QCM',
    saisie: 'Saisie', texte: 'Saisie',
    tableau_vf: 'Vrai/Faux', vf_figure: 'Vrai/Faux',
    programme: 'Construction',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'position_point',     label: L_POSITION,       formatsList: buildFormats('position_point') },
    { id: 'classement_points',  label: L_CLASSEMENT_VF,  formatsList: buildFormats('classement_points') },
    { id: 'zone_double',        label: L_ZONE_DOUBLE,    formatsList: buildFormats('zone_double') },
    { id: 'compter_points_zone', label: L_COMPTER,       formatsList: buildFormats('compter_points_zone') },
    { id: 'ensemble_points',    label: L_CONSTRUCTION,   formatsList: buildFormats('ensemble_points') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genPositionPointQcmEnsemble,
    genClassementPointsVfFigure,
    genZoneDoubleQcmFigure,
    genCompterPointsZoneSaisie,
    genEnsemblePointsProgramme,
};
